import {
  collection,
  query,
  where,
  onSnapshot,
  orderBy,
  doc,
  getDoc,
  getDocs,
  updateDoc,
  limit,
  startAt,
  endAt,
} from "firebase/firestore";
import { useEffect, useState } from "react";
import { db } from "../firebase";
import { useAuth } from "../context/AuthContext";
import ChatRoom from "./ChatRoom";
import { ensureUserHasPlan } from "../utils/planUtils";
import AntonyModalContent from "./IA Antony/A_IA_Modal";

export default function ChatList() {
  const { currentUser } = useAuth();
  const [chats, setChats] = useState([]);
  const [activeChatUser, setActiveChatUser] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [loadingUser, setLoadingUser] = useState(false);
  const [userDetails, setUserDetails] = useState({});
  const [error, setError] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0); // Added to trigger refresh

  // Mensajes para la IA
  const mensajes = [
    "Habla con Antony 🤖",
    "¿Tienes dudas? Pregúntale a Antony",
    "Antony puede ayudarte",
    "¡Antony está disponible!",
    "Hazle una pregunta a Antony",
  ];

  const [mensajeVisible, setMensajeVisible] = useState(false);
  const [mensajeActual, setMensajeActual] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(false);

  useEffect(() => {
    const mostrarMensaje = () => {
      const nuevo = mensajes[Math.floor(Math.random() * mensajes.length)];
      setMensajeActual(nuevo);
      setMensajeVisible(true);
      setTimeout(() => setMensajeVisible(false), 4000);
    };

    mostrarMensaje();
    const interval = setInterval(mostrarMensaje, 20000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (!currentUser?.uid) return;

    const qChats = query(
      collection(db, "chats"),
      where("users", "array-contains", currentUser.uid)
    );
    const qPromo = query(
      collection(db, "chatsPromotional"),
      where("users", "array-contains", currentUser.uid)
    );

    const unsubChats = onSnapshot(qChats, () => {});
    const unsubPromo = onSnapshot(qPromo, () => {});

    const fetchChats = async () => {
      try {
        const [chatsSnap, promoSnap] = await Promise.all([
          getDocs(qChats),
          getDocs(qPromo),
        ]);

        const chatList = chatsSnap.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
          collection: "chats",
        }));

        const promoList = promoSnap.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
          collection: "chatsPromotional",
        }));

        const combinedChats = [...chatList, ...promoList].sort(
          (a, b) =>
            (b.lastMessageTimestamp?.seconds || 0) -
            (a.lastMessageTimestamp?.seconds || 0)
        );

        const otherUserIds = [
          ...new Set(
            combinedChats
              .map((chat) => chat.users.find((uid) => uid !== currentUser.uid))
              .filter(Boolean)
          ),
        ];

        const usersInfo = {};
        const usersSnap = await getDocs(collection(db, "users"));
        await Promise.all(
          usersSnap.docs.map(async (userDoc) => {
            const userId = userDoc.id;
            usersInfo[userId] = { uid: userId, isPromo: false, ...userDoc.data() };

            await Promise.all(
              otherUserIds.map(async (promoUid) => {
                const promoProfileRef = doc(
                  db, "users", userId, "promoProfiles", promoUid
                );
                const promoSnap = await getDoc(promoProfileRef);
                if (promoSnap.exists()) {
                  usersInfo[promoUid] = { uid: promoUid, isPromo: true, ...promoSnap.data() };
                }
              })
            );
          })
        );

        setUserDetails(usersInfo);
        setChats(combinedChats);
        setError(null);
      } catch (error) {
        console.error("Error fetching chats:", error);
        setError("Error al cargar los chats. Inténtalo de nuevo.");
      }
    };

    fetchChats();

    return () => {
      unsubChats();
      unsubPromo();
    };
  }, [currentUser?.uid, refreshKey]); // Added refreshKey to dependencies

  useEffect(() => {
    if (!searchTerm.trim()) {
      setSearchResults([]);
      return;
    }

    const usersRef = collection(db, "users");

    const qByName = query(
      usersRef,
      orderBy("nombre"),
      startAt(searchTerm),
      endAt(searchTerm + "\uf8ff"),
      limit(5)
    );

    const qByNickname = query(
      usersRef,
      orderBy("nickname"),
      startAt(searchTerm),
      endAt(searchTerm + "\uf8ff"),
      limit(5)
    );

    const unsubName = onSnapshot(qByName, (snapNombre) => {
      const byName = snapNombre.docs
        .filter((doc) => doc.id !== currentUser.uid)
        .map((doc) => ({ uid: doc.id, isPromo: false, ...doc.data() }));

      const unsubNick = onSnapshot(qByNickname, (snapNickname) => {
        const byNickname = snapNickname.docs
          .filter((doc) => doc.id !== currentUser.uid)
          .map((doc) => ({ uid: doc.id, isPromo: false, ...doc.data() }));

        // Combinar sin duplicados
        const all = [...byName, ...byNickname];
        const unique = Array.from(
          new Map(all.map((item) => [item.uid, item])).values()
        );

        setSearchResults(unique);
      });

      return () => {
        unsubNick();
      };
    });

    return () => {
      unsubName();
    };
  }, [searchTerm, currentUser?.uid]);

  const fetchOtherUserData = async (chat) => {
    const otherUserId = chat.users.find((uid) => uid !== currentUser.uid);
    if (!otherUserId) {
      console.error("No other user ID found in chat:", chat);
      setError("No se pudo cargar el chat. Inténtalo de nuevo.");
      return;
    }

    setLoadingUser(true);
    try {
      await ensureUserHasPlan(currentUser.uid);

      let userData = null;
      if (chat.collection === "chats") {
        const docRef = doc(db, "users", otherUserId);
        const snap = await getDoc(docRef);
        if (snap.exists()) {
          userData = { uid: otherUserId, isPromo: false, ...snap.data() };
        }
      } else {
        const usersSnap = await getDocs(collection(db, "users"));
        for (const userDoc of usersSnap.docs) {
          const promoProfileRef = doc(
            db, "users", userDoc.id, "promoProfiles", otherUserId
          );
          const promoSnap = await getDoc(promoProfileRef);
          if (promoSnap.exists()) {
            userData = { uid: otherUserId, isPromo: true, ...promoSnap.data() };
            break;
          }
        }
      }

      if (userData) {
        setActiveChatUser(userData);
        const chatRef = doc(db, chat.collection, chat.id);
        await updateDoc(chatRef, {
          [`readStatus.${currentUser.uid}`]: true,
        });
      } else {
        console.error("User data not found for ID:", otherUserId);
        setError("No se encontró el usuario para este chat.");
      }
    } catch (error) {
      console.error("Error fetching other user data:", error);
      setError("Error al cargar el chat. Por favor, intenta de nuevo.");
    } finally {
      setLoadingUser(false);
    }
  };

  const formatTimestamp = (timestamp) => {
    if (!timestamp?.toDate) return "";

    const date = timestamp.toDate();
    const now = new Date();

    const diffMs = now - date;
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    const optionsTime = {
      hour: "numeric",
      minute: "numeric",
      hour12: true,
    };

    const daysOfWeek = [
      "domingo",
      "lunes",
      "martes",
      "miércoles",
      "jueves",
      "viernes",
      "sábado",
    ];

    const capitalize = (s) => s.charAt(0).toUpperCase() + s.slice(1);

    if (date.toDateString() === now.toDateString()) {
      return `Hoy a las ${date.toLocaleTimeString("es-ES", optionsTime)}`;
    } else if (
      date.toDateString() ===
      new Date(now.getFullYear(), now.getMonth(), now.getDate() - 1).toDateString()
    ) {
      return `Ayer a las ${date.toLocaleTimeString("es-ES", optionsTime)}`;
    } else if (diffDays < 7) {
      const dayName = daysOfWeek[date.getDay()];
      return `El ${capitalize(dayName)} pasado a las ${date.toLocaleTimeString(
        "es-ES",
        optionsTime
      )}`;
    } else {
      const day = date.getDate();
      const month = date.toLocaleString("es-ES", { month: "short" });
      return `${day} de ${month} a las ${date.toLocaleTimeString(
        "es-ES",
        optionsTime
      )}`;
    }
  };

  const openChatWithUser = (user) => {
    setActiveChatUser(user);
  };

  const closeChat = () => {
    setActiveChatUser(null);
    setRefreshKey((prev) => prev + 1); // Trigger refresh when closing ChatRoom
  };

  if (activeChatUser)
    return <ChatRoom otherUser={activeChatUser} onClose={closeChat} />;

  return (
    <div className="p-4 space-y-4 max-w-2xl mx-auto sm:px-6 md:px-8">
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
          Tus chats
        </h2>
        <div className="relative">
          <img
            src="https://res.cloudinary.com/dnmygv8u6/image/upload/v1751361560/ChatGPT_Image_1_jul_2025_05_18_48_rac8yz.png"
            alt="Antony"
            className="w-10 h-10 sm:w-12 sm:h-12 rounded-full cursor-pointer border shadow-lg hover:scale-105 transition-transform"
            onClick={() => setIsModalOpen(true)}
          />
          {mensajeVisible && (
            <div className="absolute -top-12 right-0 bg-white dark:bg-gray-800 text-sm px-3 py-2 rounded shadow-md border animate-fade-in-out z-50 whitespace-nowrap">
              {mensajeActual}
            </div>
          )}
        </div>
      </div>

      {isModalOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-40 backdrop-blur-sm flex items-center justify-center z-50"
          aria-modal="true"
          role="dialog"
        >
          <div
            className="
              bg-white dark:bg-gray-900 rounded-2xl shadow-2xl max-w-md w-full
              transform transition-transform duration-300 ease-out
              scale-100 animate-fadeInScale
              flex flex-col relative
            "
          >
            <div className="flex justify-between items-center p-4 border-b border-gray-300 dark:border-gray-700">
              <h3 className="font-semibold text-lg text-gray-900 dark:text-gray-100 select-none">
                Asistente <span className="text-blue-600 dark:text-blue-400">Antony</span>
              </h3>
              <button
                onClick={() => {
                  if (
                    window.confirm(
                      "¿Seguro que quieres cerrar la conversación? Se perderá el historial."
                    )
                  ) {
                    setIsModalOpen(false);
                  }
                }}
                className="text-gray-500 hover:text-red-600 dark:hover:text-red-400 transition text-2xl font-bold select-none"
                aria-label="Cerrar chat"
                title="Cerrar conversación"
              >
                ×
              </button>
            </div>

            <AntonyModalContent onClose={() => setIsModalOpen(false)} />

            <p className="text-center text-sm text-red-600 dark:text-red-400 font-semibold py-2 select-none">
              ⚠️ Al cerrar la conversación, esta no se guardará.
            </p>

            <style>{`
              @keyframes fadeInScale {
                0% {
                  opacity: 0;
                  transform: scale(0.9);
                }
                100% {
                  opacity: 1;
                  transform: scale(1);
                }
              }
              .animate-fadeInScale {
                animation: fadeInScale 0.3s ease forwards;
              }
            `}</style>
          </div>
        </div>
      )}

      <input
        type="text"
        placeholder="Buscar usuario para chatear..."
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        className="w-full p-3 border rounded-lg dark:bg-gray-800 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition"
      />

      {error && <p className="text-red-500">{error}</p>}

      {searchResults.length > 0 && (
        <div className="border rounded-lg p-2 bg-white dark:bg-gray-800 shadow max-h-60 overflow-auto">
          {searchResults.map((user) => (
            <button
              key={user.uid}
              onClick={() => openChatWithUser(user)}
              className="w-full text-left p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded"
            >
              <div className="flex items-center space-x-3">
                {user.photoURL && (
                  <img
                    src={user.photoURL}
                    alt="avatar"
                    className="w-8 h-8 rounded-full object-cover"
                  />
                )}
                <span className="text-gray-800 dark:text-white text-sm">
                  {user.nickname || user.nombre || user.email}
                </span>
              </div>
            </button>
          ))}
        </div>
      )}

      {loadingUser && (
        <p className="text-gray-500 dark:text-gray-300">Cargando chat...</p>
      )}

      <div className="space-y-3">
        {chats.length === 0 && (
          <p className="text-gray-500 dark:text-gray-400">No tienes chats todavía.</p>
        )}

        {chats.map((chat) => {
          const otherUid = chat.users.find((uid) => uid !== currentUser.uid);
          const otherUser = userDetails[otherUid];
          const isUnread = !chat.readStatus?.[currentUser.uid];
          const lastMessage = chat.lastMessage || "Sin mensajes aún";
          const lastMessageTime = formatTimestamp(chat.lastMessageTimestamp);

          return (
            <button
              key={chat.id}
              onClick={() => fetchOtherUserData(chat)}
              className="w-full flex items-center p-4 bg-white dark:bg-gray-800 rounded-xl shadow hover:bg-gray-50 dark:hover:bg-gray-700 transition-all overflow-hidden"
            >
              {otherUser?.profilePhotoUrl || otherUser?.photoURL ? (
                <img
                  src={otherUser.profilePhotoUrl || otherUser.photoURL}
                  alt="avatar"
                  className="w-10 h-10 rounded-full object-cover mr-4 flex-shrink-0"
                />
              ) : (
                <div className="w-10 h-10 rounded-full bg-gray-300 dark:bg-gray-600 mr-4 flex items-center justify-center text-gray-600 dark:text-gray-300 font-bold flex-shrink-0">
                  {(otherUser?.name || otherUser?.nickname)?.charAt(0).toUpperCase() || "U"}
                </div>
              )}

              <div className="flex-1 min-w-0 flex flex-col justify-between">
                <div className="flex justify-between items-center gap-x-2 min-w-0">
                  <p className="font-semibold text-gray-900 dark:text-white truncate text-sm sm:text-base min-w-0">
                    {otherUser?.name || otherUser?.nickname || otherUser?.nombre || "Usuario desconocido"}
                    {chat.collection === "chatsPromotional" && (
                      <span className="text-xs text-yellow-500 ml-2">(Promocional)</span>
                    )}
                  </p>
                  {chat.lastMessageTimestamp && (
                    <span className="text-xs text-gray-500 dark:text-gray-400 shrink-0">
                      {lastMessageTime}
                    </span>
                  )}
                </div>

                <div className="flex items-center space-x-2 min-w-0">
                  <p
                    className={`text-sm truncate min-w-0 ${
                      isUnread
                        ? "font-semibold text-gray-900 dark:text-white"
                        : "text-gray-600 dark:text-gray-400"
                    }`}
                  >
                    {lastMessage}
                  </p>
                  {isUnread && (
                    <span className="text-blue-500 text-xs shrink-0">(Nuevo Mensaje)</span>
                  )}
                </div>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}